import gsap, { Power2, Power3 } from 'gsap';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import { Ease } from '../_app/cuchillo/utils/Ease';
import { Maths } from '../_app/cuchillo/utils/Maths';
import ScrollItem__BlockDefault from './ScrollItem__BlockDefault';
import Scene from '../3D/Scene';
import { LIQUID_SETTINGS, sphereIsMobile } from '../3D/LiquidSphere';

/**
 * BlockParticlesFake
 * Intro de título + imagen scrubbeada con el scroll.
 * Los tags empiezan a rotar cuando termina la última línea del título.
 */
class ScrollItem__BlockParticlesFake extends ScrollItem__BlockDefault {

    // -------------------------------------------------------------------------
    // Tags (intervalo de aparición aleatoria)
    // -------------------------------------------------------------------------
    static INTERVAL = 800;   // ms entre tags
    static MAX_VISIBLE = 3;

    // -------------------------------------------------------------------------
    // Intro — this.progress → timeline 0→1 (ajustable)
    // Las duraciones son proporciones del timeline, no tiempo real:
    // el scroll las escanea según INTRO_RANGE.
    // -------------------------------------------------------------------------
    static INTRO_RANGE = [0.15, 1];

    static INTRO_Y_DURATION = 2.4;
    static INTRO_ALPHA_DURATION = 0.85; // más corto que Y → el fade se siente más rápido
    static INTRO_STAGGER = 0.75;
    static INTRO_FIRST_Y_FROM = 300;
    static INTRO_FIRST_Y_MULT = 3; // 1ª línea: duración Y ×3

    // Rotación leve. El origin queda en el extremo opuesto para que caiga el lado libre.
    static INTRO_ROT_MIN = 3;
    static INTRO_ROT_MAX = 8;
    static INTRO_ROT_FIRST_MIN = 1;
    static INTRO_ROT_FIRST_MAX = 3;
    static INTRO_ORIGIN_EDGE = 18; // % de ancho cerca del extremo (0…EDGE / 100-EDGE…100)

    // Progreso del timeline (0→1) a partir del cual salen los tags.
    // El fin “matemático” de la última línea suele ser ~1 y el scrub casi no lo alcanza.
    static TAGS_TRIGGER_AT = 0.6;

    // Esfera: mass radius (spawnRadius) de SPHERE_RADIUS_FROM al valor de LIQUID_SETTINGS dins d'aquest rang de progress.
    // Rang i mida inicial per plataforma. Mobile: el rang cau dins la fase sticky del .ball (≈0.31 → 0.62).
    // Mobile es decideix en temps d'ús (sphereIsMobile), no en carregar el mòdul — veure LiquidSphere.js.
    static SPHERE_RADIUS_FROM_DESKTOP = 1;
    static SPHERE_RADIUS_RANGE_DESKTOP = [0, 0.3];
    static SPHERE_RADIUS_FROM_MOBILE = 0.6;
    static SPHERE_RADIUS_RANGE_MOBILE = [0.45, 0.6];

    // =========================================================================
    // Constructor
    // =========================================================================

    constructor(__link, __index, __scroller) {
        super(__link, __index, __scroller);

        // Área donde pueden aparecer los tags (se actualiza en resize)
        this.rectangle = {
            x: Metrics.WIDTH * 0.1,
            y: Metrics.HEIGHT * 0.1,
            width: Metrics.WIDTH * 0.8,
            height: Metrics.HEIGHT * 0.8,
        };

        this.tags = [...this.item.querySelectorAll('.__tag')]
            .map(el => new ScrollItem__BlockParticlesFake__Tag(el));
        this.visibleTags = [];
        this.timer = null;

        this._introTl = null;
        this._tagsTriggerAt = 1; // progreso del timeline (0→1) para showTags

        this._setupIntro(__link);

        // Esfera de partícules (2n render) centrada al .ball
        if (this._ball) {
            Scene.addSphere(this._ball);
            this._animateSphere();
        }

        this.onShow = () => { };
        this.onHide = () => { this.hideTags(); };
        this.onMove = () => {
            this._scrubIntro();
            this._animateSphere();
        };
    }

    // =========================================================================
    // Intro — setup y timeline
    // =========================================================================

    _setupIntro(__link) {
        const title = GetBy.class('title', __link)[0];
        this._titleLines = title ? [...title.children] : [];

        this._ball = GetBy.class('ball', __link)[0];
        this._ballImg = this._ball ? GetBy.tag('img', this._ball)[0] : null;

        this._buildIntroTl();
    }

    _animateSphere() {
        if (!this._ball) return;

        const C = ScrollItem__BlockParticlesFake;
        const mobile = sphereIsMobile();
        const from = mobile ? C.SPHERE_RADIUS_FROM_MOBILE : C.SPHERE_RADIUS_FROM_DESKTOP;
        const [start, end] = mobile ? C.SPHERE_RADIUS_RANGE_MOBILE : C.SPHERE_RADIUS_RANGE_DESKTOP;
        const progress = Number.isFinite(this.progress) ? this.progress : 0;
        const p = Maths.clamp(Maths.normalize(end, start, progress), 0, 1);

        // Passat el rang, sense override: mana el valor del pane (es pot afinar en viu).
        Scene.setSphereRadius(p >= 1
            ? null
            : Maths.lerp(from, LIQUID_SETTINGS.spawnRadius, p));
    }

    _buildIntroTl() {
        if (this._introTl) {
            this._introTl.kill();
            this._introTl = null;
        }

        const lines = this._titleLines;
        if (!lines.length) return;

        const stagger = ScrollItem__BlockParticlesFake.INTRO_STAGGER;
        const yDur = ScrollItem__BlockParticlesFake.INTRO_Y_DURATION;
        const yDurFirst = yDur * ScrollItem__BlockParticlesFake.INTRO_FIRST_Y_MULT;
        const alphaDur = ScrollItem__BlockParticlesFake.INTRO_ALPHA_DURATION;
        const firstYFrom = ScrollItem__BlockParticlesFake.INTRO_FIRST_Y_FROM;

        // Estado inicial
        if (this._ballImg) gsap.set(this._ballImg, { autoAlpha: 0, yPercent: 0 });
        if (this._ball) gsap.set(this._ball, { yPercent: 0 });

        const tl = gsap.timeline({ paused: true });
        this._introTl = tl;

        // 1) Líneas del título
        this._addTitleLineTweens(tl, lines, { stagger, yDurFirst, alphaDur, firstYFrom });

        // Duración de referencia para el movimiento de ball / imagen
        const totalDur = Math.max((lines.length - 1) * stagger + yDur, yDurFirst);
        const secondStart = stagger;
        const moveDur = Math.max(0.01, totalDur - secondStart);

        // 2) Contenedor .ball (solo Y; no toca el alpha de la img)
        this._addBallTweens(tl, { secondStart, moveDur });

        // 3) Imagen (alpha + Y independientes)
        this._addImageTweens(tl, {
            lines,
            stagger,
            alphaDur,
            totalDur,
            secondStart,
            moveDur,
        });

        // Tags: umbral en progreso del intro (ajustable en TAGS_TRIGGER_AT)
        this._tagsTriggerAt = ScrollItem__BlockParticlesFake.TAGS_TRIGGER_AT;
    }

    /** Entrada de cada línea: Y + rotación + alpha (stagger). */
    _addTitleLineTweens(tl, lines, { stagger, yDurFirst, alphaDur, firstYFrom }) {
        const edge = ScrollItem__BlockParticlesFake.INTRO_ORIGIN_EDGE;

        lines.forEach((line, i) => {
            const at = i * stagger;
            const isFirst = i === 0;

            const lineYDur = yDurFirst;
            const lineYFrom = firstYFrom;
            const rotMin = isFirst
                ? ScrollItem__BlockParticlesFake.INTRO_ROT_FIRST_MIN
                : ScrollItem__BlockParticlesFake.INTRO_ROT_MIN;
            const rotMax = isFirst
                ? ScrollItem__BlockParticlesFake.INTRO_ROT_FIRST_MAX
                : ScrollItem__BlockParticlesFake.INTRO_ROT_MAX;

            // Rotación aleatoria ±; origin en el extremo opuesto
            const sign = Math.random() < 0.5 ? -1 : 1;
            const rot = sign * (rotMin + Math.random() * (rotMax - rotMin));
            const originX = rot > 0
                ? Math.random() * edge
                : 100 - Math.random() * edge;

            gsap.set(line, {
                autoAlpha: 0,
                yPercent: lineYFrom,
                transformOrigin: `${originX}% 100%`,
            });

            tl.fromTo(
                line,
                { yPercent: lineYFrom, rotation: rot },
                {
                    yPercent: 0,
                    rotation: 0,
                    duration: lineYDur,
                    ease: Ease.EASE_CUCHILLO_OUT,
                },
                at
            );

            tl.fromTo(
                line,
                { autoAlpha: 0 },
                {
                    autoAlpha: 1,
                    duration: alphaDur,
                    ease: Power3.easeIn,
                },
                at
            );
        });
    }

    /** Parallax Y del .ball (misma ventana que la imagen). */
    _addBallTweens(tl, { secondStart, moveDur }) {
        if (!this._ball) return;

        tl.fromTo(
            this._ball,
            { yPercent: 10 },
            {
                yPercent: -5,
                duration: moveDur,
                ease: 'none',
            },
            secondStart
        );
    }

    /** Fade de la imagen + su propio movimiento Y. */
    _addImageTweens(tl, { lines, stagger, alphaDur, totalDur, secondStart, moveDur }) {
        if (!this._ballImg) return;

        // Alpha: casi al final del fade de la 1ª línea → inicio de la 3ª (o fin)
        const firstAlmostDone = alphaDur * 0.9;
        const thirdStart = lines.length >= 3 ? 2 * stagger : totalDur;
        const fadeStart = Math.min(firstAlmostDone, thirdStart - 0.01);
        const fadeDur = Math.max(0.01, thirdStart - fadeStart);

        tl.fromTo(
            this._ballImg,
            { autoAlpha: 0 },
            {
                autoAlpha: 1,
                duration: fadeDur,
                ease: Power2.easeOut,
            },
            fadeStart
        );

        tl.fromTo(
            this._ballImg,
            { yPercent: 0 },
            {
                yPercent: -10,
                duration: moveDur,
                ease: 'none',
            },
            secondStart
        );
    }

    // =========================================================================
    // Intro — scrub con el scroll
    // =========================================================================

    _scrubIntro() {
        if (!this._introTl) return;

        // Mapea this.progress dentro de INTRO_RANGE → 0…1 del timeline
        const p = Maths.clamp(
            Maths.normalize(
                ScrollItem__BlockParticlesFake.INTRO_RANGE[1],
                ScrollItem__BlockParticlesFake.INTRO_RANGE[0],
                this.progress
            ),
            0,
            1
        );

        this._introTl.progress(p);

        // Tags: show al pasar el umbral; hide solo si ya estaban activos
        if (p >= this._tagsTriggerAt) this.showTags();
        else if (this.timer) this.hideTags();
    }

    // =========================================================================
    // Tags
    // =========================================================================

    showTags() {
        return;
        if (this.timer) return;

        this.showNextTag();
        this.timer = setInterval(
            () => this.showNextTag(),
            ScrollItem__BlockParticlesFake.INTERVAL
        );
    }

    hideTags() {
        clearInterval(this.timer);
        this.timer = null;

        this.visibleTags.forEach(tag => tag.hide());
        this.visibleTags.length = 0;
    }

    showNextTag() {
        const available = this.tags.filter(tag => !tag.visible);

        if (!available.length) return;

        const tag = available[Math.floor(Math.random() * available.length)];
        tag.show(this.rectangle);
        this.visibleTags.push(tag);


        if (this.visibleTags.length > ScrollItem__BlockParticlesFake.MAX_VISIBLE) {
            const oldest = this.visibleTags.shift();
            oldest.hide();
        }
    }

    // =========================================================================
    // Ciclo de vida
    // =========================================================================

    resize(__w, __h) {
        super.resize(__w, __h);

        this.rectangle = {
            x: Metrics.WIDTH * 0.1,
            y: Metrics.HEIGHT * 0.1,
            width: Metrics.WIDTH * 0.8,
            height: Metrics.HEIGHT * 0.8,
        };

        this.tags.forEach(tag => tag.resize());
        this._scrubIntro();
        this._animateSphere();
    }

    dispose() {
        if (this._introTl) {
            this._introTl.kill();
            this._introTl = null;
        }
        this.hideTags();
        if (this._ball) Scene.removeSphere(this._ball);
        super.dispose();
    }
}

// =============================================================================
// Tag individual
// =============================================================================

class ScrollItem__BlockParticlesFake__Tag {

    constructor(el) {
        this.el = el;
        this.width = 0;
        this.height = 0;
        this.visible = false;

        this.hide(true);
    }

    resize() {
        this.width = this.el.offsetWidth;
        this.height = this.el.offsetHeight;
    }

    /** Posición aleatoria dentro de rect + fade in. */
    show(rect) {
        this.visible = true;

        const maxX = Math.max(0, rect.width - this.width);
        const maxY = Math.max(0, rect.height - this.height);
        const x = rect.x + Math.random() * maxX;
        const y = rect.y + Math.random() * maxY;

        this.el.style.transform = `translate3d(${x}px, ${y}px, 0)`;

        gsap.to(this.el, { opacity: 1, duration: 0.2, ease: Power2.easeIn });


        gsap.to(this.el, { opacity: 1, duration: .2, ease: Power2.easeIn });

    }

    hide(immediate = false) {
        this.visible = false;

        gsap.killTweensOf(this.el);
        gsap.set(this.el, { opacity: 0 });

        if (immediate) {
            this.el.style.transform = `translate3d(-9999px, -9999px, 0)`;
        }
    }
}

Scroll._registerClass('BlockParticlesFake', ScrollItem__BlockParticlesFake);

export default ScrollItem__BlockParticlesFake;
